import React from "react";
import { Col, Row, Button } from "react-bootstrap";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import SearchFilter from "../CustomUIComponents/SearchFilter/SearchFilter";
import { SEARCH_FILTER_TYPE, SEARCH_FILTER_FIELDS } from "../../constants/appConstants";
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
} from "@mui/material";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import TextFieldComponent from "../CustomUIComponents/TextFieldComponent/TextFieldComponent";
import { Strings } from "../../resources/i18n/i18n";
import { OPERATION_STATUS } from "../../config/axios.init";
import { fixLoanFormFilledAction } from "./DatabaseTableAction";
import "./DatabaseTables.scss";
import {
  StyledHeadCell,
  StyledBodyCell,
  StyledRow,
  formatValue,
  getMaxId,
  DUPLICATE_TABS as TABS,
  fetchByTab,
  deleteByTab,
  updateByTab,
} from "./Constants";

// ── Component ────────────────────────────────────────────────────────────────

class DuplicateEntryManagement extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      searchValue: "",
      searchError: "",
      loading: false,
      activeTab: 0,
      // keyed by tab.key — undefined = not yet searched, null/data = searched result
      tabData: {
        approvalAssignment: [],
        remittanceDelivery: [],
        loanApplication: [],
        loanProcess: [],
        notes: [],
      },
      // type: "delete" | "fix"
      confirmDialog: { open: false, type: null, tabKey: null, id: null, label: "" },
      infoDialog: { open: false, rowData: null },
      editDialog: { open: false, tabKey: null, id: null, values: {} },
      actionLoading: false,
      successMessage: "",
      errorMessage: "",
    };
  }

  handleSearch = () => {
    const { searchValue, activeTab } = this.state;
    const tab = TABS[activeTab];

    if (!searchValue.toString().trim()) {
      this.setState({ errorMessage: Strings("enterApplicationId") });
      return;
    }

    this.setState(
      (prev) => ({
        loading: true,
        searchError: "",
        successMessage: "",
        errorMessage: "",
        tabData: { ...prev.tabData, [tab.key]: undefined },
      }),
      () => {
        fetchByTab(tab.key, searchValue, (response) => {
          if (response.status === OPERATION_STATUS.SUCCESS) {
            this.setState((prev) => ({
              loading: false,
              tabData: { ...prev.tabData, [tab.key]: response.data.data },
            }));
          } else {
            this.setState({
              loading: false,
              errorMessage: response.error?.message || Strings("errorFetchingData"),
            });
          }
        });
      }
    );
  };

  handleReset = () => {
    this.setState({
      searchValue: "",
      loading: false,
      successMessage: "",
      errorMessage: "",
      tabData: {
        approvalAssignment: [],
        remittanceDelivery: [],
        loanApplication: [],
        loanProcess: [],
        notes: [],
      },
    });
  };

  handleTabChange = (newIndex) => {
    const { activeTab, searchValue } = this.state;
    const currentParam = TABS[activeTab].searchParam;
    const newTab = TABS[newIndex];
    const paramChanged = currentParam !== newTab.searchParam;
    const newSearchValue = paramChanged ? "" : searchValue;

    this.setState(
      {
        activeTab: newIndex,
        searchValue: newSearchValue,
        searchError: "",
        successMessage: "",
        errorMessage: "",
      },
      () => {
        // Auto-fetch when switching tabs if a search value exists
        if (newSearchValue) {
          this.setState({ loading: true }, () => {
            fetchByTab(newTab.key, newSearchValue, (response) => {
              if (response.status === OPERATION_STATUS.SUCCESS) {
                this.setState((prev) => ({
                  loading: false,
                  tabData: { ...prev.tabData, [newTab.key]: response.data.data },
                }));
              } else {
                this.setState({
                  loading: false,
                  errorMessage: response.error?.message || Strings("errorFetchingData"),
                });
              }
            });
          });
        }
      }
    );
  };

  // Delete Duplicate: shows max-ID row in confirmation
  handleDeleteDuplicateClick = (tab, rows) => {
    const maxId = getMaxId(rows, tab.idField);
    this.setState({
      confirmDialog: {
        open: true,
        type: "delete",
        tabKey: tab.key,
        id: maxId,
        label: `${tab.idField}: ${maxId}`,
      },
    });
  };

  // Row level delete: used by tables where every row is deletable on its own (notes)
  handleDeleteRowClick = (tab, row) => {
    const id = row[tab.idField];
    this.setState({
      confirmDialog: {
        open: true,
        type: "delete",
        tabKey: tab.key,
        id,
        label: `${tab.idField}: ${id}`,
      },
    });
  };

  // Fix form filled (is_form_filled = 0 → 1)
  handleFixFormFilledClick = (applicationId) => {
    this.setState({
      confirmDialog: {
        open: true,
        type: "fix",
        tabKey: "loanApplication",
        id: applicationId,
        label: `${Strings("applicationIdLabel")}: ${applicationId}`,
      },
    });
  };

  handleConfirmClose = () => {
    this.setState({ confirmDialog: { open: false, type: null, tabKey: null, id: null, label: "" } });
  };

  handleConfirmOk = () => {
    const { confirmDialog, searchValue } = this.state;
    const { type, tabKey, id } = confirmDialog;
    this.setState({
      actionLoading: true,
      confirmDialog: { open: false, type: null, tabKey: null, id: null, label: "" },
    });

    const isRowDelete = !!TABS.find((t) => t.key === tabKey)?.rowDelete;

    const callBack = (response) => {
      if (response.status === OPERATION_STATUS.SUCCESS) {
        let msg;
        if (type === "fix") {
          msg = Strings("Form set to filled successfully..");
        } else {
          msg = isRowDelete
            ? Strings("reversalDeleteSuccess")
            : Strings("duplicate Deleted Successfully..");
        }
        this.setState(
          { actionLoading: false, successMessage: msg, errorMessage: "" },
          () => {
            // Re-fetch only the affected tab
            fetchByTab(tabKey, searchValue, (r) => {
              if (r.status === OPERATION_STATUS.SUCCESS) {
                this.setState((prev) => ({
                  tabData: { ...prev.tabData, [tabKey]: r.data.data },
                }));
              }
            });
          }
        );
      } else {
        this.setState({
          actionLoading: false,
          errorMessage: response.error?.message || Strings("errorFetchingData"),
        });
      }
    };

    if (type === "fix") {
      fixLoanFormFilledAction(id, callBack);
    } else {
      deleteByTab(tabKey, id, callBack);
    }
  };

  handleInfoClick = (rowData) => {
    this.setState({ infoDialog: { open: true, rowData } });
  };

  handleInfoClose = () => {
    this.setState({ infoDialog: { open: false, rowData: null } });
  };

  // ── Edit (loan_application) ────────────────────────────────────────────────

  handleEditClick = (tab, row) => {
    const values = {};
    (tab.editableFields || []).forEach((field) => {
      const value = row[field.key];
      if (value === null || value === undefined) {
        values[field.key] = "";
      } else if (field.type === "date") {
        // HTML date inputs need yyyy-MM-dd
        const text = String(value);
        values[field.key] = /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : text;
      } else if (typeof value === "object") {
        values[field.key] = JSON.stringify(value);
      } else {
        values[field.key] = value;
      }
    });
    this.setState({
      editDialog: { open: true, tabKey: tab.key, id: row[tab.idField], values },
    });
  };

  handleEditClose = () => {
    this.setState({ editDialog: { open: false, tabKey: null, id: null, values: {} } });
  };

  handleEditFieldChange = (key, value) => {
    this.setState((prev) => ({
      editDialog: { ...prev.editDialog, values: { ...prev.editDialog.values, [key]: value } },
    }));
  };

  handleEditSubmit = () => {
    const { editDialog, searchValue } = this.state;
    const { tabKey, id, values } = editDialog;
    this.setState({ actionLoading: true });

    updateByTab(tabKey, id, values, (response) => {
      if (response.status === OPERATION_STATUS.SUCCESS) {
        this.setState(
          {
            actionLoading: false,
            successMessage: Strings("duplicateUpdateSuccess"),
            errorMessage: "",
            editDialog: { open: false, tabKey: null, id: null, values: {} },
          },
          () => {
            fetchByTab(tabKey, searchValue, (r) => {
              if (r.status === OPERATION_STATUS.SUCCESS) {
                this.setState((prev) => ({
                  tabData: { ...prev.tabData, [tabKey]: r.data.data },
                }));
              }
            });
          }
        );
      } else {
        this.setState({
          actionLoading: false,
          errorMessage: response.error?.message || Strings("errorFetchingData"),
        });
      }
    });
  };

  // ── Renders ────────────────────────────────────────────────────────────────

  handleSearchValueChange = (event) => {
    this.setState({ searchValue: event.target.value, errorMessage: "" });
  };

  renderSearchFilter = () => {
    const { searchValue } = this.state;
    const filters = [
      {
        type: SEARCH_FILTER_TYPE.INPUT,
        searchPlaceHolder: Strings("applicationId"),
        handleOnChange: this.handleSearchValueChange,
        Value: searchValue,
        inputType: SEARCH_FILTER_TYPE.NUMBER,
        fieldname: SEARCH_FILTER_FIELDS.APPLICATION_ID,
        paddingTop: true,
      },
    ];

    return (
      <SearchFilter
        filters={filters}
        handleOnClickSearchFilter={this.handleSearch}
        handleOnClickResetFilter={this.handleReset}
        paddingRight={0}
        paddingTop={2}
        removeLeftPadding={true}
      />
    );
  };

  renderMessages = () => {
    const { successMessage, errorMessage } = this.state;
    return (
      <>
        {successMessage && (
          <Row className="mb-2">
            <Col>
              <div className="successMessage">{successMessage}</div>
            </Col>
          </Row>
        )}
        {errorMessage && (
          <Row className="mb-2">
            <Col>
              <div className="errors p-2 errorMessage">{errorMessage}</div>
            </Col>
          </Row>
        )}
      </>
    );
  };

  // Multi-row table: shows all rows + "Delete Duplicate" button (targets max-ID row)
  renderMultiRowTable = (rows, tab) => {
    if (!rows || rows.length === 0) {
      return (
        <div className="noRecordsFound noBranchRecords">{Strings("NoRecordsFound")}</div>
      );
    }

    const idField = tab.idField
    const maxId = getMaxId(rows, idField);
    // ID column always first so it's always visible
    const keys = [idField, ...Object.keys(rows[0] || {}).filter(k => k !== idField)];

    return (
      <>
        <Row className="align-items-center mb-2">
          <Col>
            <span className="listLabel">
              {tab.rowDelete ? (
                Strings("rowsListTitle")
              ) : (
                <>
                  {Strings("duplicatesListTitle")}&nbsp;
                  <Chip
                    label={`${Strings("maxIdLabel")}: ${maxId}`}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                </>
              )}
            </span>
          </Col>
          {!tab.rowDelete && (
            <Col xs="auto">
              <Button
                size="sm"
                variant="danger"
                onClick={() => this.handleDeleteDuplicateClick(tab, rows)}
              >
                <DeleteSweepIcon fontSize="small" style={{ marginRight: 4, verticalAlign: "middle" }} />
                {Strings("delete Duplicate")}
              </Button>
            </Col>
          )}
        </Row>

        <TableContainer component={Paper} sx={{ border: "1px solid #ccc", maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {keys.map((k) => <StyledHeadCell key={k}>{k}</StyledHeadCell>)}
                <StyledHeadCell align="center" sx={{ width: tab.rowDelete ? 100 : 60 }}>
                  {tab.rowDelete ? Strings("actionsLabel") : "Details"}
                </StyledHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <StyledRow
                  key={row[idField] ?? idx}
                  sx={
                    !tab.rowDelete && row[idField] === maxId
                      ? { backgroundColor: "#be3c3c7d !important" }
                      : {}
                  }
                >
                  {keys.map((k) => (
                    <StyledBodyCell key={k} title={formatValue(row[k])}>
                      {formatValue(row[k])}
                    </StyledBodyCell>
                  ))}
                  <StyledBodyCell align="center">
                    {tab.rowDelete && (
                      <Tooltip title={Strings("Delete")}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => this.handleDeleteRowClick(tab, row)}
                        >
                          <DeleteSweepIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={Strings("rowDetails")}>
                      <IconButton size="small" onClick={() => this.handleInfoClick(row)} color="info">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </StyledBodyCell>
                </StyledRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  // Key-value table for loanApplication (single record)
  // Shows "Fix Form Filled" button if is_form_filled == 0, plus "Edit" when the tab is editable
  renderSingleRecord = (record, searchValue, tab) => {
    if (!record) {
      return (
        <div className="noRecordsFound noBranchRecords">{Strings("NoRecordsFound")}</div>
      );
    }

    const showFixButton = record.is_form_filled === 0;
    const showEditButton = !!(tab.editableFields || []).length;
    const entries = Object.entries(record);

    return (
      <>
        {(showFixButton || showEditButton) && (
          <Row className="mb-2">
            {showFixButton && (
              <Col xs="auto">
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => this.handleFixFormFilledClick(searchValue)}
                >
                  <TaskAltIcon fontSize="small" style={{ marginRight: 4, verticalAlign: "middle" }} />
                  {"Set is_Form_Filled 1"}
                </Button>
              </Col>
            )}
            {showEditButton && (
              <Col xs="auto">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => this.handleEditClick(tab, record)}
                >
                  <EditIcon fontSize="small" style={{ marginRight: 4, verticalAlign: "middle" }} />
                  {Strings("Edit")}
                </Button>
              </Col>
            )}
          </Row>
        )}

        <TableContainer component={Paper} sx={{ border: "1px solid #ccc", maxHeight: 400 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledHeadCell sx={{ width: "35%" }}>{Strings("fieldLabel")}</StyledHeadCell>
                <StyledHeadCell>{Strings("valueLabel")}</StyledHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map(([k, v]) => (
                <StyledRow key={k}>
                  <StyledBodyCell sx={{ fontWeight: "bold" }}>{k}</StyledBodyCell>
                  <StyledBodyCell>{formatValue(v)}</StyledBodyCell>
                </StyledRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  renderTabContent = (tab) => {
    const { tabData, searchValue, loading } = this.state;
    const data = tabData[tab.key];

    if (loading) {
      return <div className="text-center py-4"><CircularProgress size={28} /></div>;
    }

    if (data === undefined) {
      return (
        <div className="noRecordsFound noBranchRecords searchPrompt">
          {Strings("duplicateSearchPrompt")}
        </div>
      );
    }

    // null or empty = searched but no data returned
    if (data === null || (Array.isArray(data) ? data.length === 0 : Object.keys(data).length === 0)) {
      return (
        <div className="noRecordsFound noBranchRecords searchPrompt">
          {Strings("NoRecordsFound")}
        </div>
      );
    }

    if (!tab.multi) {
      const record = Array.isArray(data) ? data[0] : data;
      return this.renderSingleRecord(record, searchValue, tab);
    }
    return this.renderMultiRowTable(data, tab);
  };

  getDeleteConfirmationText = (tabKey) => {
    return TABS.find((t) => t.key === tabKey)?.rowDelete
      ? Strings("reversalDeleteConfirm")
      : Strings("Delete Duplicate Confirmation");
  };

  renderConfirmDialog = () => {
    const { confirmDialog, actionLoading } = this.state;
    const isDelete = confirmDialog.type === "delete";

    return (
      <Dialog open={confirmDialog.open} onClose={this.handleConfirmClose} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ fontSize: 15, fontWeight: "bold", color: isDelete ? "#c62828" : "#1b5e20" }}
        >
          {isDelete ? Strings("confirmDelete") : Strings("confirmFix")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            {isDelete
              ? this.getDeleteConfirmationText(confirmDialog.tabKey)
              : Strings("Fix Form Filled Confirmation")}
          </DialogContentText>
          <Chip
            label={confirmDialog.label}
            size="small"
            color={isDelete ? "error" : "success"}
            sx={{ mt: 1.5, fontWeight: "bold" }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={this.handleConfirmClose}
            disabled={actionLoading}
          >
            {Strings("Cancel")}
          </Button>
          <Button
            size="sm"
            variant={isDelete ? "danger" : "success"}
            onClick={this.handleConfirmOk}
            disabled={actionLoading}
            style={{ minWidth: 80 }}
          >
            {actionLoading
              ? <CircularProgress size={14} color="inherit" />
              : isDelete ? Strings("DELETE") : Strings("Set")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  getFieldInputType = (field) => {
    if (field.type === "enum") {
      return SEARCH_FILTER_TYPE.SELECT;
    }
    if (field.type === "number") {
      return SEARCH_FILTER_TYPE.NUMBER;
    }
    if (field.type === "date") {
      return "date";
    }
    return "text";
  };

  // Keeps the current DB value selectable even if it is not in the known enum list.
  getEnumOptions = (field, currentValue) => {
    const options = field.options || [];
    if (currentValue && !options.includes(currentValue)) {
      return [currentValue, ...options];
    }
    return options;
  };

  renderEditField = (field) => {
    const value = this.state.editDialog.values[field.key];
    const isEnum = field.type === "enum";

    return (
      <Col xs={12} md={6} className="mb-3 px-2 reversalEditField" key={field.key}>
        <div className="reversalEditLabel">{field.key}</div>
        <TextFieldComponent
          id={`duplicate-field-${field.key}`}
          type={this.getFieldInputType(field)}
          value={value === undefined || value === null ? "" : value}
          handleChange={(event) => this.handleEditFieldChange(field.key, event.target.value)}
          halfWidth={false}
        >
          {isEnum && [
            <option key="__none" value="">
              {Strings("none")}
            </option>,
            ...this.getEnumOptions(field, value).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            )),
          ]}
        </TextFieldComponent>
      </Col>
    );
  };

  renderEditDialog = () => {
    const { editDialog, actionLoading } = this.state;
    if (!editDialog.open) {
      return null;
    }
    const tab = TABS.find((t) => t.key === editDialog.tabKey);
    const fields = tab?.editableFields || [];

    return (
      <Dialog open={editDialog.open} onClose={this.handleEditClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: "bold" }}>
          {`${Strings("Edit")} — ${tab?.label} (${tab?.idField}: ${editDialog.id})`}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ "overflowX": "hidden", "& .row": { marginLeft: 0, marginRight: 0 } }}
        >
          <Row>{fields.map((field) => this.renderEditField(field))}</Row>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={this.handleEditClose}
            disabled={actionLoading}
          >
            {Strings("Cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={this.handleEditSubmit}
            disabled={actionLoading}
            style={{ minWidth: 80 }}
          >
            {actionLoading ? <CircularProgress size={14} color="inherit" /> : Strings("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  renderInfoDialog = () => {
    const { infoDialog } = this.state;
    if (!infoDialog.rowData) return null;
    const entries = Object.entries(infoDialog.rowData);

    return (
      <Dialog open={infoDialog.open} onClose={this.handleInfoClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 14, fontWeight: "bold" }}>{Strings("rowDetails")}</DialogTitle>
        <DialogContent dividers>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledHeadCell sx={{ width: "35%" }}>{Strings("fieldLabel")}</StyledHeadCell>
                  <StyledHeadCell>{Strings("valueLabel")}</StyledHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map(([k, v]) => (
                  <StyledRow key={k}>
                    <StyledBodyCell sx={{ fontWeight: "bold", fontSize: 11, color: "#893f3f" }}>{k}</StyledBodyCell>
                    <StyledBodyCell sx={{ fontSize: 11, wordBreak: "break-all" }}>{formatValue(v)}</StyledBodyCell>
                  </StyledRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="sm" variant="outline-secondary" onClick={this.handleInfoClose}>
            {Strings("Cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  render() {
    const { activeTab } = this.state;

    return (
      <div className="duplicateEntryManagement">
        <div className="headerText">
          {Strings("DuplicateEntryManagement")}
        </div>

        <Row className="paper">
          <Col md={12} className="pb-2">
            {this.renderSearchFilter()}
          </Col>
          {this.renderMessages()}
          <Col md={12} className="ps-0 pe-0 pb-2 pt-2">
            <hr className="newhr opacity-100" />
          </Col>

          <Tabs selectedIndex={activeTab} onSelect={this.handleTabChange}>
            <TabList>
              {TABS.map((tab, i) => (
                <Tab
                  key={tab.key}
                  className={
                    activeTab === i
                      ? "react-tabs__tab react-tabs__tab--selected mt-1"
                      : "react-tabs__tab mt-1"
                  }
                >
                  {Strings(tab.label)}
                </Tab>
              ))}
            </TabList>

            {TABS.map((tab) => (
              <TabPanel key={tab.key}>
                <Row className="pt-2">
                  <Col md={12}>{this.renderTabContent(tab)}</Col>
                </Row>
              </TabPanel>
            ))}
          </Tabs>
        </Row>

        {this.renderConfirmDialog()}
        {this.renderEditDialog()}
        {this.renderInfoDialog()}
      </div>
    );
  }
}

export default DuplicateEntryManagement;
